import {
  Glass,
  Dot,
  ScreenLabel,
  DisplayText,
  BodyText,
  alpha,
  borderAlpha,
} from "../../components/mobile/primitives";

// ── Step 1: Capture Sheet — Note mode ───────────────────────────────────────

export function FlowCaptureText() {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
        position: "relative",
      }}
    >
      {/* Dimmed backdrop */}
      <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.3)", zIndex: 0 }} />
      <div style={{ flex: "0 0 38%", zIndex: 1 }} />

      {/* Bottom sheet */}
      <div
        style={{
          flex: 1,
          zIndex: 2,
          display: "flex",
          flexDirection: "column",
          background:
            "linear-gradient(180deg, rgba(248,245,238,0.98) 0%, rgba(245,243,236,0.99) 100%)",
          borderRadius: "24px 24px 0 0",
          boxShadow: "0 -8px 32px -8px rgba(0,0,0,0.12)",
          overflow: "hidden",
        }}
      >
        {/* Drag handle */}
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            padding: "10px 0 6px",
            flexShrink: 0,
          }}
        >
          <div
            style={{
              width: 40,
              height: 5,
              borderRadius: 3,
              background: "var(--color-kin-divider)",
            }}
          />
        </div>

        {/* Input bar with text */}
        <div style={{ padding: "4px 16px 0", flexShrink: 0 }}>
          <Glass
            style={{
              padding: "10px 10px 10px 16px",
              display: "flex",
              alignItems: "flex-end",
              gap: 10,
              borderRadius: 28,
            }}
          >
            <span
              style={{
                flex: 1,
                fontSize: 14,
                fontWeight: 500,
                color: "var(--color-kin-black)",
                fontFamily: "'Cormorant Garamond', Georgia, serif",
                lineHeight: 1.4,
              }}
            >
              Took 5000 IU vitamin D with breakfast. Knee feeling better after physio yesterday.
            </span>
            <div
              style={{
                width: 30,
                height: 30,
                borderRadius: "50%",
                background: "var(--color-kin-green-soft)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                boxShadow: "0 2px 8px rgba(91,122,102,0.4)",
              }}
            >
              <span
                style={{
                  color: "var(--color-kin-white)",
                  fontSize: 14,
                  fontWeight: 800,
                  lineHeight: 1,
                }}
              >
                ↑
              </span>
            </div>
          </Glass>
        </div>

        {/* Mode selector — Note active */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-evenly",
            padding: "14px 12px 10px",
            flexShrink: 0,
          }}
        >
          {[
            { id: "note", label: "Note", active: true },
            { id: "voice", label: "Voice", active: false },
            { id: "photo", label: "Photo", active: false },
            { id: "file", label: "File", active: false },
            { id: "scan", label: "Scan", active: false },
          ].map((mode) => (
            <div
              key={mode.id}
              style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}
            >
              <div
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: "50%",
                  background: mode.active ? "rgba(91,122,102,0.14)" : "transparent",
                  border: mode.active ? "1px solid rgba(91,122,102,0.2)" : "1px solid transparent",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <ModeIcon
                  id={mode.id}
                  color={mode.active ? "var(--color-kin-green-soft)" : "var(--color-kin-muted)"}
                />
              </div>
              <ScreenLabel
                style={{
                  fontSize: 7,
                  letterSpacing: "0.12em",
                  color: mode.active ? "var(--color-kin-green-soft)" : "var(--color-kin-muted)",
                  fontWeight: mode.active ? 800 : 700,
                }}
              >
                {mode.label}
              </ScreenLabel>
            </div>
          ))}
        </div>

        {/* Divider */}
        <div
          style={{ height: 1, background: "rgba(0,0,0,0.04)", margin: "0 16px", flexShrink: 0 }}
        />

        {/* Hint */}
        <div style={{ padding: "14px 20px", textAlign: "center" }}>
          <BodyText
            size={9}
            color="var(--color-kin-muted)"
            weight={500}
            style={{ lineHeight: 1.5 }}
          >
            Record a symptom, supplement, meal, or anything health-related.
          </BodyText>
        </div>
      </div>
    </div>
  );
}

// ── Step 1b: Capture Sheet — Expanded with trackers ─────────────────────────

export function FlowCaptureExpanded() {
  const trackers = [
    {
      color: "#ff6b6b",
      label: "Symptom",
      detail: "Headache, fatigue, pain...",
      icon: "symptom",
    },
    {
      color: "var(--color-kin-lavender)",
      label: "Mood",
      detail: "How are you feeling?",
      icon: "mood",
    },
    {
      color: "var(--color-kin-green-soft)",
      label: "Activity",
      detail: "Exercise, steps, workout...",
      icon: "activity",
    },
    {
      color: "var(--color-kin-gold)",
      label: "Food",
      detail: "Meals, snacks, drinks...",
      icon: "food",
    },
    {
      color: "#52b4d9",
      label: "Supplement",
      detail: "Vitamins, medications...",
      icon: "supplement",
    },
    {
      color: "#ff8a4d",
      label: "Measurement",
      detail: "Weight, blood pressure...",
      icon: "measurement",
    },
  ];

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
        position: "relative",
      }}
    >
      {/* Dimmed backdrop — very small since sheet is expanded */}
      <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.3)", zIndex: 0 }} />
      <div style={{ flex: "0 0 8%", zIndex: 1 }} />

      {/* Expanded bottom sheet */}
      <div
        style={{
          flex: 1,
          zIndex: 2,
          display: "flex",
          flexDirection: "column",
          background:
            "linear-gradient(180deg, rgba(248,245,238,0.98) 0%, rgba(245,243,236,0.99) 100%)",
          borderRadius: "24px 24px 0 0",
          boxShadow: "0 -8px 32px -8px rgba(0,0,0,0.12)",
          overflow: "hidden",
        }}
      >
        {/* Drag handle */}
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            padding: "10px 0 6px",
            flexShrink: 0,
          }}
        >
          <div
            style={{
              width: 40,
              height: 5,
              borderRadius: 3,
              background: "var(--color-kin-divider)",
            }}
          />
        </div>

        {/* Input bar — empty, ready for input */}
        <div style={{ padding: "4px 16px 0", flexShrink: 0 }}>
          <Glass
            style={{
              padding: "10px 10px 10px 16px",
              display: "flex",
              alignItems: "flex-end",
              gap: 10,
              borderRadius: 28,
            }}
          >
            <span
              style={{
                flex: 1,
                fontSize: 13,
                fontWeight: 400,
                color: "var(--color-kin-muted)",
                fontFamily: "'Cormorant Garamond', Georgia, serif",
                lineHeight: 1.4,
              }}
            >
              What would you like to log?
            </span>
            <div
              style={{
                width: 30,
                height: 30,
                borderRadius: "50%",
                background: "var(--color-kin-divider)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <span
                style={{
                  color: "var(--color-kin-white)",
                  fontSize: 14,
                  fontWeight: 800,
                  lineHeight: 1,
                }}
              >
                ↑
              </span>
            </div>
          </Glass>
        </div>

        {/* Mode selector — Note active */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-evenly",
            padding: "14px 12px 10px",
            flexShrink: 0,
          }}
        >
          {[
            { id: "note", label: "Note", active: true },
            { id: "voice", label: "Voice", active: false },
            { id: "photo", label: "Photo", active: false },
            { id: "file", label: "File", active: false },
            { id: "scan", label: "Scan", active: false },
          ].map((mode) => (
            <div
              key={mode.id}
              style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}
            >
              <div
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: "50%",
                  background: mode.active ? "rgba(91,122,102,0.14)" : "transparent",
                  border: mode.active ? "1px solid rgba(91,122,102,0.2)" : "1px solid transparent",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <ModeIcon
                  id={mode.id}
                  color={mode.active ? "var(--color-kin-green-soft)" : "var(--color-kin-muted)"}
                />
              </div>
              <ScreenLabel
                style={{
                  fontSize: 7,
                  letterSpacing: "0.12em",
                  color: mode.active ? "var(--color-kin-green-soft)" : "var(--color-kin-muted)",
                  fontWeight: mode.active ? 800 : 700,
                }}
              >
                {mode.label}
              </ScreenLabel>
            </div>
          ))}
        </div>

        {/* Divider */}
        <div
          style={{ height: 1, background: "rgba(0,0,0,0.04)", margin: "0 16px", flexShrink: 0 }}
        />

        {/* Quick Trackers — 3×2 grid */}
        <div style={{ padding: "12px 16px 0", flex: 1, overflow: "hidden" }}>
          <ScreenLabel
            style={{
              letterSpacing: "0.2em",
              color: "var(--color-kin-muted)",
              marginBottom: 8,
              display: "block",
            }}
          >
            Quick Log
          </ScreenLabel>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr 1fr",
              gap: 8,
            }}
          >
            {trackers.map((t) => (
              <Glass
                key={t.label}
                style={{
                  padding: "10px 6px",
                  borderRadius: 14,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 5,
                }}
              >
                <div
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: 8,
                    background: alpha(t.color, "14"),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <TrackerIcon id={t.icon} color={t.color} />
                </div>
                <BodyText size={9} weight={700} color="var(--color-kin-black)">
                  {t.label}
                </BodyText>
              </Glass>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Step 1c: Capture Sheet — Supplement tracker ─────────────────────────────

export function FlowCaptureSupplement() {
  const stack = [
    { name: "Vitamin D", dose: "5000 IU", color: "var(--color-kin-gold)", logged: true },
    { name: "Omega-3", dose: "1000 mg", color: "#52b4d9", logged: true },
    { name: "Magnesium", dose: "400 mg", color: "var(--color-kin-lavender)", logged: false },
    { name: "Zinc", dose: "25 mg", color: "var(--color-kin-green-soft)", logged: false },
    { name: "B-Complex", dose: "1 cap", color: "#ff8a4d", logged: false },
    { name: "Creatine", dose: "5 g", color: "var(--color-kin-muted)", logged: false },
  ];

  const loggedCount = stack.filter((s) => s.logged).length;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
        position: "relative",
      }}
    >
      <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.3)", zIndex: 0 }} />
      <div style={{ flex: "0 0 8%", zIndex: 1 }} />

      <div
        style={{
          flex: 1,
          zIndex: 2,
          display: "flex",
          flexDirection: "column",
          background:
            "linear-gradient(180deg, rgba(248,245,238,0.98) 0%, rgba(245,243,236,0.99) 100%)",
          borderRadius: "24px 24px 0 0",
          boxShadow: "0 -8px 32px -8px rgba(0,0,0,0.12)",
          overflow: "hidden",
        }}
      >
        {/* Drag handle */}
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            padding: "10px 0 4px",
            flexShrink: 0,
          }}
        >
          <div
            style={{
              width: 40,
              height: 5,
              borderRadius: 3,
              background: "var(--color-kin-divider)",
            }}
          />
        </div>

        {/* Header: back + title + progress */}
        <div
          style={{
            padding: "4px 16px 10px",
            display: "flex",
            alignItems: "center",
            gap: 8,
            flexShrink: 0,
          }}
        >
          <BodyText size={11} color="var(--color-kin-muted)" weight={600}>
            ←
          </BodyText>
          <TrackerIcon id="supplement" color="#52b4d9" />
          <BodyText size={13} weight={800} color="var(--color-kin-black)">
            Supplements
          </BodyText>
          <div style={{ flex: 1 }} />
          <BodyText size={10} color="var(--color-kin-green-soft)" weight={700}>
            {loggedCount}/{stack.length}
          </BodyText>
        </div>

        {/* Your Stack — one-tap tiles */}
        <div style={{ padding: "0 16px", flexShrink: 0 }}>
          <ScreenLabel
            style={{
              letterSpacing: "0.2em",
              color: "var(--color-kin-muted)",
              marginBottom: 8,
              display: "block",
            }}
          >
            Your Stack
          </ScreenLabel>
        </div>

        <div style={{ padding: "0 16px", flex: 1, overflow: "hidden" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {stack.map((s) => (
              <Glass
                key={s.name}
                style={{
                  padding: "8px 12px",
                  borderRadius: 14,
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  opacity: s.logged ? 0.55 : 1,
                }}
              >
                {/* Tap target: checkbox */}
                <div
                  style={{
                    width: 24,
                    height: 24,
                    borderRadius: 7,
                    background: s.logged ? s.color : alpha(s.color, "14"),
                    border: s.logged ? "none" : borderAlpha(s.color, "40", "1.5px"),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    transition: "all 0.2s ease",
                  }}
                >
                  {s.logged && (
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#fff"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  )}
                </div>

                {/* Name */}
                <BodyText
                  size={11}
                  weight={700}
                  color="var(--color-kin-black)"
                  style={{
                    flex: 1,
                    textDecoration: s.logged ? "line-through" : "none",
                    textDecorationColor: "var(--color-kin-muted)",
                  }}
                >
                  {s.name}
                </BodyText>

                {/* Dose chip */}
                <div
                  style={{
                    background: s.logged ? "transparent" : alpha(s.color, "10"),
                    border: s.logged ? "1px solid rgba(0,0,0,0.06)" : borderAlpha(s.color, "25"),
                    borderRadius: 20,
                    padding: "2px 9px",
                  }}
                >
                  <BodyText
                    size={9}
                    weight={700}
                    color={s.logged ? "var(--color-kin-muted)" : s.color}
                  >
                    {s.dose}
                  </BodyText>
                </div>
              </Glass>
            ))}
          </div>

          {/* Add new */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 6,
              marginTop: 10,
            }}
          >
            <div
              style={{
                width: 22,
                height: 22,
                borderRadius: "50%",
                border: "1.5px dashed var(--color-kin-muted)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <span
                style={{
                  fontSize: 13,
                  color: "var(--color-kin-muted)",
                  lineHeight: 1,
                  fontWeight: 300,
                }}
              >
                +
              </span>
            </div>
            <BodyText size={10} weight={600} color="var(--color-kin-muted)">
              Add supplement
            </BodyText>
          </div>
        </div>

        {/* Log all remaining */}
        <div style={{ padding: "10px 16px 16px", flexShrink: 0 }}>
          <div
            style={{
              background: "var(--color-kin-green-soft)",
              borderRadius: 14,
              padding: "11px 0",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 6,
              boxShadow: "0 4px 12px rgba(91,122,102,0.3)",
            }}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#fff"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="20 6 9 17 4 12" />
            </svg>
            <BodyText size={11} weight={800} color="var(--color-kin-white)">
              Log remaining {stack.length - loggedCount}
            </BodyText>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Step 2: Capture Sheet — Voice recording ─────────────────────────────────

export function FlowCaptureVoice() {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
        position: "relative",
      }}
    >
      <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.3)", zIndex: 0 }} />
      <div style={{ flex: "0 0 38%", zIndex: 1 }} />

      <div
        style={{
          flex: 1,
          zIndex: 2,
          display: "flex",
          flexDirection: "column",
          background:
            "linear-gradient(180deg, rgba(248,245,238,0.98) 0%, rgba(245,243,236,0.99) 100%)",
          borderRadius: "24px 24px 0 0",
          boxShadow: "0 -8px 32px -8px rgba(0,0,0,0.12)",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            padding: "10px 0 6px",
            flexShrink: 0,
          }}
        >
          <div
            style={{
              width: 40,
              height: 5,
              borderRadius: 3,
              background: "var(--color-kin-divider)",
            }}
          />
        </div>

        {/* Input bar — disabled in voice mode */}
        <div style={{ padding: "4px 16px 0", flexShrink: 0 }}>
          <Glass style={{ padding: "10px 10px 10px 16px", borderRadius: 28, opacity: 0.5 }}>
            <span
              style={{
                fontSize: 13,
                fontWeight: 400,
                color: "var(--color-kin-muted)",
                fontFamily: "'Manrope', system-ui",
              }}
            >
              Add a note...
            </span>
          </Glass>
        </div>

        {/* Mode selector — Voice active */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-evenly",
            padding: "14px 12px 10px",
            flexShrink: 0,
          }}
        >
          {[
            { id: "note", label: "Note", active: false },
            { id: "voice", label: "Voice", active: true },
            { id: "photo", label: "Photo", active: false },
            { id: "file", label: "File", active: false },
            { id: "scan", label: "Scan", active: false },
          ].map((mode) => (
            <div
              key={mode.id}
              style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}
            >
              <div
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: "50%",
                  background: mode.active ? "rgba(165,144,255,0.14)" : "transparent",
                  border: mode.active
                    ? "1px solid rgba(165,144,255,0.25)"
                    : "1px solid transparent",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <ModeIcon
                  id={mode.id}
                  color={mode.active ? "var(--color-kin-lavender)" : "var(--color-kin-muted)"}
                />
              </div>
              <ScreenLabel
                style={{
                  fontSize: 7,
                  letterSpacing: "0.12em",
                  color: mode.active ? "var(--color-kin-lavender)" : "var(--color-kin-muted)",
                  fontWeight: mode.active ? 800 : 700,
                }}
              >
                {mode.label}
              </ScreenLabel>
            </div>
          ))}
        </div>

        <div
          style={{ height: 1, background: "rgba(0,0,0,0.04)", margin: "0 16px", flexShrink: 0 }}
        />

        {/* Recording state — pulsing ring + timer */}
        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 14,
          }}
        >
          <div style={{ position: "relative", width: 72, height: 72 }}>
            {/* Pulse ring */}
            <div
              style={{
                position: "absolute",
                inset: -4,
                borderRadius: "50%",
                border: "2px solid rgba(165,144,255,0.3)",
                animation: "pulse 1.2s ease-in-out infinite alternate",
              }}
            />
            {/* Stop button */}
            <div
              style={{
                width: 72,
                height: 72,
                borderRadius: "50%",
                background: "#ff6b6b",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 4px 16px -4px rgba(255,107,107,0.4)",
              }}
            >
              <div style={{ width: 18, height: 18, borderRadius: 4, background: "#fff" }} />
            </div>
          </div>
          <BodyText
            size={18}
            weight={600}
            color="var(--color-kin-black)"
            style={{ fontFamily: "'SF Mono', 'Menlo', monospace", letterSpacing: 1 }}
          >
            0:12
          </BodyText>
          <BodyText size={9} color="var(--color-kin-muted)" weight={500}>
            Recording...
          </BodyText>
        </div>
      </div>
    </div>
  );
}

// ── Step 3: Capture Sheet — Photo with preview ──────────────────────────────

export function FlowCapturePhoto() {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
        position: "relative",
      }}
    >
      <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.3)", zIndex: 0 }} />
      <div style={{ flex: "0 0 20%", zIndex: 1 }} />

      <div
        style={{
          flex: 1,
          zIndex: 2,
          display: "flex",
          flexDirection: "column",
          background:
            "linear-gradient(180deg, rgba(248,245,238,0.98) 0%, rgba(245,243,236,0.99) 100%)",
          borderRadius: "24px 24px 0 0",
          boxShadow: "0 -8px 32px -8px rgba(0,0,0,0.12)",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            padding: "10px 0 6px",
            flexShrink: 0,
          }}
        >
          <div
            style={{
              width: 40,
              height: 5,
              borderRadius: 3,
              background: "var(--color-kin-divider)",
            }}
          />
        </div>

        {/* Mode selector — Photo active */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-evenly",
            padding: "8px 12px 10px",
            flexShrink: 0,
          }}
        >
          {[
            { id: "note", label: "Note", active: false },
            { id: "voice", label: "Voice", active: false },
            { id: "photo", label: "Photo", active: true },
            { id: "file", label: "File", active: false },
            { id: "scan", label: "Scan", active: false },
          ].map((mode) => (
            <div
              key={mode.id}
              style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}
            >
              <div
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: "50%",
                  background: mode.active ? "rgba(91,122,102,0.14)" : "transparent",
                  border: mode.active ? "1px solid rgba(91,122,102,0.2)" : "1px solid transparent",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <ModeIcon
                  id={mode.id}
                  color={mode.active ? "var(--color-kin-green-soft)" : "var(--color-kin-muted)"}
                />
              </div>
              <ScreenLabel
                style={{
                  fontSize: 7,
                  letterSpacing: "0.12em",
                  color: mode.active ? "var(--color-kin-green-soft)" : "var(--color-kin-muted)",
                  fontWeight: mode.active ? 800 : 700,
                }}
              >
                {mode.label}
              </ScreenLabel>
            </div>
          ))}
        </div>

        <div
          style={{ height: 1, background: "rgba(0,0,0,0.04)", margin: "0 16px", flexShrink: 0 }}
        />

        {/* Photo preview — lab report */}
        <div style={{ padding: "12px 16px", display: "flex", flexDirection: "column", gap: 10 }}>
          <div
            style={{
              height: 160,
              borderRadius: 16,
              background: "linear-gradient(135deg, #f5f0e8 0%, #ede8df 100%)",
              border: "1px solid rgba(0,0,0,0.06)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
              overflow: "hidden",
            }}
          >
            {/* Simulated lab report document */}
            <div style={{ width: "70%", display: "flex", flexDirection: "column", gap: 4 }}>
              <div
                style={{ height: 6, background: "rgba(0,0,0,0.12)", borderRadius: 3, width: "60%" }}
              />
              <div
                style={{ height: 4, background: "rgba(0,0,0,0.06)", borderRadius: 2, width: "80%" }}
              />
              <div style={{ height: 1, background: "rgba(0,0,0,0.08)", margin: "4px 0" }} />
              {[...Array(5)].map((_, i) => (
                <div key={i} style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                  <div
                    style={{ height: 3, background: "rgba(0,0,0,0.06)", borderRadius: 2, flex: 2 }}
                  />
                  <div
                    style={{
                      height: 3,
                      background: "rgba(91,122,102,0.2)",
                      borderRadius: 2,
                      flex: 1,
                    }}
                  />
                  <div
                    style={{ height: 3, background: "rgba(0,0,0,0.04)", borderRadius: 2, flex: 1 }}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Caption input */}
          <Glass
            style={{
              padding: "8px 12px 8px 14px",
              borderRadius: 20,
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <span
              style={{
                flex: 1,
                fontSize: 12,
                fontWeight: 500,
                color: "var(--color-kin-black)",
                fontFamily: "'Manrope', system-ui",
              }}
            >
              Blood panel from Dr. Chen
            </span>
            <div
              style={{
                width: 28,
                height: 28,
                borderRadius: "50%",
                background: "var(--color-kin-green-soft)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <span style={{ color: "#fff", fontSize: 12, fontWeight: 800 }}>↑</span>
            </div>
          </Glass>

          <BodyText
            size={9}
            color="var(--color-kin-lavender)"
            weight={600}
            style={{ textAlign: "center" }}
          >
            Retake
          </BodyText>
        </div>
      </div>
    </div>
  );
}

// ── Step 4: Enrichment in progress — vault file processing ──────────────────

export function FlowEnrichmentProgress() {
  return (
    <div style={{ padding: "8px 16px 0", display: "flex", flexDirection: "column", gap: 12 }}>
      {/* Greeting */}
      <div style={{ paddingLeft: 4, display: "flex", flexDirection: "column", gap: 1 }}>
        <ScreenLabel>Thu, March 20</ScreenLabel>
        <DisplayText size={26}>Good morning,</DisplayText>
        <DisplayText size={26} color="var(--color-kin-green-soft)">
          <span style={{ fontStyle: "italic" }}>Sverre</span>
        </DisplayText>
      </div>

      {/* Featured Insight */}
      <div style={{ position: "relative" }}>
        <div
          style={{
            position: "absolute",
            inset: -4,
            background:
              "radial-gradient(circle at 100% 0%, rgba(165,144,255,0.35), transparent 60%)",
            filter: "blur(12px)",
            borderRadius: 24,
          }}
        />
        <Glass
          style={{
            position: "relative",
            zIndex: 1,
            padding: "14px 16px",
            background:
              "linear-gradient(150deg, rgba(254,253,251,0.7) 0%, rgba(254,253,251,0.4) 100%)",
            border: "1px solid rgba(255,255,255,0.6)",
            borderRadius: 20,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
            <Dot
              color="var(--color-kin-lavender)"
              size={6}
              style={{ boxShadow: "0 0 8px var(--color-kin-lavender)" }}
            />
            <ScreenLabel style={{ color: "var(--color-kin-lavender)", letterSpacing: "0.2em" }}>
              Latest Insight
            </ScreenLabel>
          </div>
          <DisplayText size={18}>Sleep Analysis</DisplayText>
          <BodyText
            size={10}
            color="var(--color-kin-muted)"
            weight={500}
            style={{ marginTop: 4, lineHeight: 1.4 }}
          >
            Updated 3h ago
          </BodyText>
        </Glass>
      </div>

      {/* Processing card — file being enriched */}
      <div>
        <div style={{ marginBottom: 8, paddingLeft: 4 }}>
          <ScreenLabel style={{ letterSpacing: "0.2em" }}>Processing</ScreenLabel>
        </div>
        <Glass style={{ padding: "14px 16px", borderRadius: 20 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            {/* Spinner */}
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: 10,
                background: "rgba(165,144,255,0.1)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <div
                style={{
                  width: 14,
                  height: 14,
                  borderRadius: "50%",
                  border: "2px solid rgba(165,144,255,0.2)",
                  borderTopColor: "var(--color-kin-lavender)",
                }}
              />
            </div>
            <div style={{ flex: 1 }}>
              <BodyText size={11} weight={700} color="var(--color-kin-black)">
                Blood Panel Mar 2026
              </BodyText>
              <BodyText
                size={9}
                color="var(--color-kin-lavender)"
                weight={600}
                style={{ marginTop: 2 }}
              >
                Analysing · extracting biomarkers...
              </BodyText>
            </div>
          </div>
          {/* Progress bar */}
          <div
            style={{
              height: 3,
              borderRadius: 2,
              background: "rgba(0,0,0,0.04)",
              marginTop: 12,
              overflow: "hidden",
            }}
          >
            <div
              style={{
                width: "65%",
                height: "100%",
                borderRadius: 2,
                background: "linear-gradient(90deg, var(--color-kin-lavender), #c4b8ff)",
              }}
            />
          </div>
        </Glass>
      </div>

      {/* Recent */}
      <div>
        <div style={{ marginBottom: 8, paddingLeft: 4 }}>
          <ScreenLabel style={{ letterSpacing: "0.2em" }}>Recent</ScreenLabel>
        </div>
        <Glass style={{ padding: "4px 14px", borderRadius: 20 }}>
          {[
            {
              dot: "var(--color-kin-gold)",
              title: "Took 5000 IU vitamin D with break...",
              sub: "Note · just now",
            },
            {
              dot: "var(--color-kin-green-soft)",
              title: "Oura Sleep Export",
              sub: "Wearable · 2d ago",
            },
          ].map((item, i) => (
            <div
              key={i}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "9px 0",
                borderBottom: i < 1 ? "1px solid rgba(0,0,0,0.03)" : "none",
              }}
            >
              <Dot color={item.dot} size={6} />
              <div style={{ flex: 1 }}>
                <BodyText
                  size={10}
                  weight={600}
                  style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                >
                  {item.title}
                </BodyText>
                <BodyText
                  size={9}
                  color="var(--color-kin-muted)"
                  weight={500}
                  style={{ marginTop: 1 }}
                >
                  {item.sub}
                </BodyText>
              </div>
            </div>
          ))}
        </Glass>
      </div>
    </div>
  );
}

// ── Step 5: Home with "From Your Data" insights ─────────────────────────────

export function FlowHomeWithInsights() {
  return (
    <div style={{ padding: "8px 16px 0", display: "flex", flexDirection: "column", gap: 12 }}>
      {/* Greeting */}
      <div style={{ paddingLeft: 4, display: "flex", flexDirection: "column", gap: 1 }}>
        <ScreenLabel>Thu, March 20</ScreenLabel>
        <DisplayText size={26}>Good morning,</DisplayText>
        <DisplayText size={26} color="var(--color-kin-green-soft)">
          <span style={{ fontStyle: "italic" }}>Sverre</span>
        </DisplayText>
      </div>

      {/* Featured Insight */}
      <div style={{ position: "relative" }}>
        <div
          style={{
            position: "absolute",
            inset: -4,
            background:
              "radial-gradient(circle at 100% 0%, rgba(165,144,255,0.35), transparent 60%)",
            filter: "blur(12px)",
            borderRadius: 24,
          }}
        />
        <Glass
          style={{
            position: "relative",
            zIndex: 1,
            padding: "14px 16px",
            background:
              "linear-gradient(150deg, rgba(254,253,251,0.7) 0%, rgba(254,253,251,0.4) 100%)",
            border: "1px solid rgba(255,255,255,0.6)",
            borderRadius: 20,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
            <Dot
              color="var(--color-kin-lavender)"
              size={6}
              style={{ boxShadow: "0 0 8px var(--color-kin-lavender)" }}
            />
            <ScreenLabel style={{ color: "var(--color-kin-lavender)", letterSpacing: "0.2em" }}>
              Latest Insight
            </ScreenLabel>
          </div>
          <DisplayText size={18}>Sleep Analysis</DisplayText>
          <BodyText size={10} color="var(--color-kin-muted)" weight={500} style={{ marginTop: 4 }}>
            Updated 3h ago
          </BodyText>
        </Glass>
      </div>

      {/* From Your Data — source entries with extracted facts */}
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <div style={{ paddingLeft: 4 }}>
          <ScreenLabel style={{ letterSpacing: "0.2em" }}>From Your Data</ScreenLabel>
        </div>

        {/* Blood Panel — file with extracted biomarkers */}
        <Glass style={{ padding: "12px 14px", borderRadius: 20 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div
              style={{
                width: 30,
                height: 30,
                borderRadius: 9,
                background: alpha("var(--color-kin-green-soft)", "14"),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <InsightIcon type="doc" color="var(--color-kin-green-soft)" />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <BodyText size={11} weight={700} color="var(--color-kin-black)">
                Blood Panel Mar 2026
              </BodyText>
              <BodyText size={9} color="var(--color-kin-muted)" weight={500}>
                Lab report · PDF
              </BodyText>
            </div>
            <BodyText
              size={8}
              color="var(--color-kin-muted)"
              weight={500}
              style={{ flexShrink: 0 }}
            >
              12m ago
            </BodyText>
          </div>
          <div
            style={{
              marginLeft: 40,
              marginTop: 8,
              display: "flex",
              flexDirection: "column",
              gap: 6,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
              <Dot color="#ff6b6b" size={5} />
              <BodyText size={9} weight={600} color="var(--color-kin-black)">
                LDL Cholesterol
              </BodyText>
              <BodyText size={8.5} color="#ff6b6b" weight={700}>
                180 mg/dL — high
              </BodyText>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
              <Dot color="#ff8a4d" size={5} />
              <BodyText size={9} weight={600} color="var(--color-kin-black)">
                Triglycerides
              </BodyText>
              <BodyText size={8.5} color="#ff8a4d" weight={700}>
                195 mg/dL — high
              </BodyText>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
              <Dot color="var(--color-kin-green-soft)" size={5} />
              <BodyText size={9} weight={600} color="var(--color-kin-black)">
                HDL Cholesterol
              </BodyText>
              <BodyText size={8.5} color="var(--color-kin-green-soft)" weight={600}>
                62 mg/dL
              </BodyText>
            </div>
            <BodyText
              size={8.5}
              color="var(--color-kin-muted)"
              weight={600}
              style={{ marginTop: 1 }}
            >
              + 9 more markers in range
            </BodyText>
          </div>
        </Glass>

        {/* Voice note — with extracted observations */}
        <Glass style={{ padding: "12px 14px", borderRadius: 20 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div
              style={{
                width: 30,
                height: 30,
                borderRadius: 9,
                background: alpha("var(--color-kin-lavender)", "14"),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <svg
                width="13"
                height="13"
                viewBox="0 0 24 24"
                fill="none"
                stroke="var(--color-kin-lavender)"
                strokeWidth="2"
                strokeLinecap="round"
              >
                <rect x="9" y="4.5" width="6" height="11" rx="3" />
                <path d="M6.5 11.5v1a5.5 5.5 0 0 0 11 0v-1" />
                <path d="M12 18v2.5" />
              </svg>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <BodyText size={11} weight={700} color="var(--color-kin-black)">
                Voice note · 0:23
              </BodyText>
              <BodyText size={9} color="var(--color-kin-muted)" weight={500}>
                Transcribed
              </BodyText>
            </div>
            <BodyText
              size={8}
              color="var(--color-kin-muted)"
              weight={500}
              style={{ flexShrink: 0 }}
            >
              20m ago
            </BodyText>
          </div>
          <div
            style={{
              marginLeft: 40,
              marginTop: 8,
              display: "flex",
              flexDirection: "column",
              gap: 6,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
              <Dot color="var(--color-kin-lavender)" size={5} />
              <BodyText size={9} weight={600} color="var(--color-kin-black)">
                Left knee pain after running
              </BodyText>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
              <Dot color="var(--color-kin-lavender)" size={5} />
              <BodyText size={9} weight={600} color="var(--color-kin-black)">
                Physio session helped
              </BodyText>
            </div>
          </div>
        </Glass>

        {/* Text note — with extracted supplement log */}
        <Glass style={{ padding: "12px 14px", borderRadius: 20 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div
              style={{
                width: 30,
                height: 30,
                borderRadius: 9,
                background: alpha("var(--color-kin-gold)", "14"),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <svg
                width="13"
                height="13"
                viewBox="0 0 24 24"
                fill="none"
                stroke="var(--color-kin-gold)"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M6 4h12v16H6z" />
                <path d="M9 8h6" />
                <path d="M9 12h4" />
              </svg>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <BodyText size={11} weight={700} color="var(--color-kin-black)">
                Supplement note
              </BodyText>
              <BodyText size={9} color="var(--color-kin-muted)" weight={500}>
                Text note
              </BodyText>
            </div>
            <BodyText
              size={8}
              color="var(--color-kin-muted)"
              weight={500}
              style={{ flexShrink: 0 }}
            >
              25m ago
            </BodyText>
          </div>
          <div
            style={{
              marginLeft: 40,
              marginTop: 8,
              display: "flex",
              flexDirection: "column",
              gap: 6,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
              <Dot color="var(--color-kin-gold)" size={5} />
              <BodyText size={9} weight={600} color="var(--color-kin-black)">
                Vitamin D — 5000 IU
              </BodyText>
            </div>
          </div>
        </Glass>
      </div>
    </div>
  );
}

// ── Shared icons ────────────────────────────────────────────────────────────

function InsightIcon({ type, color }) {
  if (type === "flag") {
    return (
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
        <line x1="4" y1="22" x2="4" y2="15" />
      </svg>
    );
  }
  if (type === "bio") {
    return (
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
      </svg>
    );
  }
  // doc
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
    </svg>
  );
}

function ModeIcon({ id, color }) {
  const c = color;
  switch (id) {
    case "note":
      return (
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke={c}
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M6 4h12v16H6z" />
          <path d="M9 8h6" />
          <path d="M9 12h4" />
        </svg>
      );
    case "voice":
      return (
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke={c}
          strokeWidth="1.8"
          strokeLinecap="round"
        >
          <rect x="9" y="4.5" width="6" height="11" rx="3" />
          <path d="M6.5 11.5v1a5.5 5.5 0 0 0 11 0v-1" />
          <path d="M12 18v2.5" />
        </svg>
      );
    case "photo":
      return (
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke={c}
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <rect x="3.5" y="6.5" width="17" height="12" rx="2" />
          <circle cx="12" cy="12.5" r="3.3" />
          <path d="M8.2 6.5 9.4 4.6h5.2l1.2 1.9" />
        </svg>
      );
    case "file":
      return (
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke={c}
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
        </svg>
      );
    case "scan":
      return (
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke={c}
          strokeWidth="1.8"
          strokeLinecap="round"
        >
          <path d="M7 4H5.5A1.5 1.5 0 0 0 4 5.5V7" />
          <path d="M17 4h1.5A1.5 1.5 0 0 1 20 5.5V7" />
          <path d="M20 17v1.5A1.5 1.5 0 0 1 18.5 20H17" />
          <path d="M7 20H5.5A1.5 1.5 0 0 1 4 18.5V17" />
          <path d="M8 12h8" />
        </svg>
      );
    default:
      return null;
  }
}

function TrackerIcon({ id, color }) {
  const props = {
    width: "15",
    height: "15",
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: color,
    strokeWidth: "2",
    strokeLinecap: "round",
    strokeLinejoin: "round",
  };
  switch (id) {
    case "symptom":
      return (
        <svg {...props}>
          <path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z" />
          <path d="M8 15s1.5 2 4 2 4-2 4-2" />
          <circle cx="9" cy="10" r="0.5" fill={color} stroke="none" />
          <circle cx="15" cy="10" r="0.5" fill={color} stroke="none" />
        </svg>
      );
    case "mood":
      return (
        <svg {...props}>
          <circle cx="12" cy="12" r="10" />
          <path d="M8 14s1.5 2 4 2 4-2 4-2" />
          <line x1="9" y1="9" x2="9.01" y2="9" />
          <line x1="15" y1="9" x2="15.01" y2="9" />
        </svg>
      );
    case "activity":
      return (
        <svg {...props}>
          <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
        </svg>
      );
    case "food":
      return (
        <svg {...props}>
          <path d="M18 8h1a4 4 0 0 1 0 8h-1" />
          <path d="M2 8h16v9a4 4 0 0 1-4 4H6a4 4 0 0 1-4-4V8z" />
          <line x1="6" y1="1" x2="6" y2="4" />
          <line x1="10" y1="1" x2="10" y2="4" />
          <line x1="14" y1="1" x2="14" y2="4" />
        </svg>
      );
    case "supplement":
      return (
        <svg {...props}>
          <path d="M10.5 1.5H8.25A2.25 2.25 0 0 0 6 3.75v16.5A2.25 2.25 0 0 0 8.25 22.5h7.5A2.25 2.25 0 0 0 18 20.25V3.75A2.25 2.25 0 0 0 15.75 1.5H13.5" />
          <line x1="12" y1="10" x2="12" y2="16" />
          <line x1="9" y1="13" x2="15" y2="13" />
        </svg>
      );
    case "measurement":
      return (
        <svg {...props}>
          <path d="M3 3v18h18" />
          <path d="M7 16l4-4 4 4 5-6" />
        </svg>
      );
    default:
      return null;
  }
}
